/**
 * FeaturePaneShell Component
 *
 * Shared wrapper for every pane slot in the live-trading cockpit.
 * Renders a compact Card header (feature identity + extension slots)
 * and a body area for feature content (placeholder EmptyState in T10,
 * real content provided by WU2–WU6).
 *
 * Header (both breakpoints): icon-menu-trigger + label (desktop-only)
 * on left, actions slot + expand/collapse button on right. Mobile omits
 * label but keeps the same control layout.
 *
 * The feature icon acts as a menu trigger — a single Popover with
 * view management houses "Swap Feature" and "Settings" actions.
 * Arrow-key keyboard navigation between menu items is supported
 * via role="menu" / role="menuitem" semantics and a manual onKeyDown
 * handler for focus cycling.
 *
 * Swap popover view lists all features except the one currently
 * assigned, allowing the user to swap features between panes.
 * Two-way swaps (feature on another visible pane) and overflow swaps
 * (feature on hidden slot) are both supported.
 *
 * Memoized via React.memo — each pane re-renders only when its own
 * props change, not when sibling panes change. The `onSwap` callback
 * is stable (ref-based in LayoutRenderer) so it doesn't defeat memo.
 *
 * NOTE for WU2–WU6: pass stable/memoized ReactNode values for the
 * `actions` prop (e.g., via useMemo or lifted constants) to preserve
 * pane isolation provided by React.memo.
 *
 * @see constants/featureArea.ts for FEATURE_AREAS registry
 * @see types/shared/FeatureArea.ts for FeatureAreaId, FeatureAreaConfig
 * @see types/shared/Layout.ts for SlotId
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowLeftRight, ChevronLeft, Grid2x2, Maximize2, Minimize2, Settings } from 'lucide-react';

import {
  Button,
  Card, CardHeader, CardBody,
  EmptyState,
  Popover, PopoverTrigger, PopoverContent,
} from '@enfomentis/ui';
import { FEATURE_AREAS } from '@/constants/featureArea';
import { SLOT_DISPLAY_MAP } from '@/constants/layout';

import type { FeatureAreaId, SlotId } from '@/types/shared';

// ===== TYPES =====

/** Views within the icon-menu popover */
type PaneMenuView = 'menu' | 'swap' | 'settings';

interface FeaturePaneShellProps {
  /** Which slot this shell occupies */
  slotId: SlotId;
  /** Assigned feature (undefined = defensive fallback to generic pane) */
  featureAreaId: FeatureAreaId | undefined;
  /** Responsive variant — passed from LayoutRenderer */
  isDesktop: boolean;
  /** Swap callback — fires with (fromSlotId, selectedFeatureId) on popover selection */
  onSwap: (fromSlotId: SlotId, selectedFeatureId: FeatureAreaId) => void;
  /** Extension point for feature-specific header controls (empty in T10, populated by WU2–WU6) */
  actions?: React.ReactNode;
  /** Whether this pane is currently in expanded/focus mode */
  isExpanded?: boolean;
  /** Callback to toggle expand/collapse — when provided, renders the expand button */
  onExpandToggle?: (slotId: SlotId) => void;
  /** Feature body content — replaces EmptyState placeholder when provided (WU2–WU6) */
  children?: React.ReactNode;
}

// ===== COMPONENT =====

export const FeaturePaneShell: React.FC<FeaturePaneShellProps> = React.memo(({
  slotId,
  featureAreaId,
  isDesktop,
  onSwap,
  actions,
  isExpanded,
  onExpandToggle,
  children,
}) => {
  const [paneMenuView, setPaneMenuView] = useState<PaneMenuView | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleSwapSelect = useCallback((selectedFeatureId: FeatureAreaId): void => {
    onSwap(slotId, selectedFeatureId);
    setPaneMenuView(null);
  }, [onSwap, slotId]);

  // Deterministic focus on first menu item when menu view appears
  useEffect(() => {
    if (paneMenuView === 'menu') {
      const firstItem = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
      firstItem?.focus();
    }
  }, [paneMenuView]);

  // Arrow-key focus cycling between menu items
  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = (e.currentTarget).querySelectorAll<HTMLElement>('[role="menuitem"]');
    const currentIndex = Array.from(items).indexOf(e.target as HTMLElement);
    if (currentIndex === -1) return;
    const nextIndex = e.key === 'ArrowDown'
      ? (currentIndex + 1) % items.length
      : (currentIndex - 1 + items.length) % items.length;
    items[nextIndex]?.focus();
  }, []);

  // Look up feature config — defensive fallback if undefined or not found
  const featureConfig = featureAreaId !== undefined
    ? FEATURE_AREAS.find((area) => area.id === featureAreaId)
    : undefined;

  const isOpen = paneMenuView !== null;

  // ---------------------------------------------------------------------------
  // Shared popover content — renders based on paneMenuView
  // ---------------------------------------------------------------------------

  const paneMenuContent = (
    <PopoverContent className="p-1">
      {paneMenuView === 'menu' && (
        <div ref={menuRef} role="menu" aria-label="Pane options" onKeyDown={handleMenuKeyDown} className="flex flex-col gap-0.5 min-w-35">
          <Button
            role="menuitem"
            variant="light"
            size="sm"
            className="w-full justify-start"
            startContent={<ArrowLeftRight size={14} />}
            onPress={() => setPaneMenuView('swap')}
          >
            Swap Feature
          </Button>
          <Button
            role="menuitem"
            variant="light"
            size="sm"
            className="w-full justify-start"
            startContent={<Settings size={14} />}
            onPress={() => setPaneMenuView('settings')}
          >
            Settings
          </Button>
        </div>
      )}
      {paneMenuView === 'swap' && (
        <div className="flex flex-col gap-0.5 min-w-35">
          <div className="flex items-center gap-1 mb-0.5">
            <Button isIconOnly size="sm" variant="light" onPress={() => setPaneMenuView('menu')} aria-label="Back to menu">
              <ChevronLeft size={14} />
            </Button>
            <span className="text-xs font-medium text-text-secondary">Swap Feature</span>
          </div>
          {FEATURE_AREAS
            .filter((area) => area.id !== featureAreaId)
            .map((area) => (
              <Button
                key={area.id}
                variant="light"
                size="sm"
                className="w-full justify-start"
                startContent={<area.Icon size={14} className={area.iconColorClass} />}
                onPress={() => handleSwapSelect(area.id)}
              >
                {area.label}
              </Button>
            ))}
        </div>
      )}
      {paneMenuView === 'settings' && (
        <div className="min-w-35">
          <div className="flex items-center gap-1 mb-1">
            <Button isIconOnly size="sm" variant="light" onPress={() => setPaneMenuView('menu')} aria-label="Back to menu">
              <ChevronLeft size={14} />
            </Button>
            <span className="text-xs font-medium text-text-secondary">Settings</span>
          </div>
          <p className="text-xs text-text-secondary px-2 pb-2">
            Feature-specific settings will be available in future work units (WU2–WU6).
          </p>
        </div>
      )}
    </PopoverContent>
  );

  // ---------------------------------------------------------------------------
  // Fallback: unassigned or unknown feature — generic pane display
  // ---------------------------------------------------------------------------

  if (!featureConfig) {
    return (
      <Card radius="none" shadow="none" className="h-full w-full flex flex-col">
        <CardHeader className="px-2 py-1 border-b border-default-200 shrink-0">
          <div className="flex items-center justify-between w-full gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <Popover placement="bottom-start" isOpen={isOpen} onOpenChange={(open) => setPaneMenuView(open ? 'menu' : null)}>
                <PopoverTrigger>
                  <Button
                    variant="light"
                    size="sm"
                    isIconOnly
                    aria-label={`Pane options: ${SLOT_DISPLAY_MAP[slotId]}`}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                  >
                    <Grid2x2 size={16} className="text-default-400" />
                  </Button>
                </PopoverTrigger>
                {paneMenuContent}
              </Popover>
              {isDesktop && (
                <span className="text-sm text-text-secondary truncate">{SLOT_DISPLAY_MAP[slotId]}</span>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {actions}
              {onExpandToggle && (
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  className="min-h-11 min-w-11 md:min-h-0 md:min-w-0"
                  aria-label={isExpanded ? 'Collapse pane' : 'Expand pane'}
                  onPress={() => onExpandToggle(slotId)}
                >
                  {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody className={`flex-1 p-0 min-h-0 ${children ? 'overflow-hidden' : 'flex items-center justify-center'}`}>
          {children ?? (
            <EmptyState
              title={SLOT_DISPLAY_MAP[slotId]}
              description="Feature assignment unavailable"
              icon={<Grid2x2 size={24} />}
              className="py-4!"
            />
          )}
        </CardBody>
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // Assigned feature — full shell with header + body
  // ---------------------------------------------------------------------------

  const { Icon, iconColorClass, label, emptyHint } = featureConfig;

  return (
    <Card radius="none" shadow="none" className="h-full w-full flex flex-col">
      {/* Compact header bar — unified layout for desktop and mobile */}
      <CardHeader className="px-2 py-1 border-b border-default-200 shrink-0">
        <div className="flex items-center justify-between w-full gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <Popover placement="bottom-start" isOpen={isOpen} onOpenChange={(open) => setPaneMenuView(open ? 'menu' : null)}>
              <PopoverTrigger>
                <Button
                  variant="light"
                  size="sm"
                  isIconOnly
                  aria-label={`Pane options: ${label}`}
                  aria-haspopup="menu"
                  aria-expanded={isOpen}
                >
                  <Icon size={16} className={iconColorClass} />
                </Button>
              </PopoverTrigger>
              {paneMenuContent}
            </Popover>
            {isDesktop && (
              <span className="text-sm text-text-primary truncate">{label}</span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {actions}
            {onExpandToggle && (
              <Button
                isIconOnly
                size="sm"
                variant="light"
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0"
                aria-label={isExpanded ? 'Collapse pane' : 'Expand pane'}
                onPress={() => onExpandToggle(slotId)}
              >
                {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Body — EmptyState placeholder replaced by feature content when children provided (WU2–WU6) */}
      <CardBody className={`flex-1 p-0 min-h-0 ${children ? 'overflow-hidden' : 'flex items-center justify-center'}`}>
        {children ?? (
          <EmptyState
            title={`${label} — Coming Soon`}
            description={emptyHint}
            icon={<Icon size={24} className={iconColorClass} />}
            className="py-4!"
          />
        )}
      </CardBody>
    </Card>
  );
});

FeaturePaneShell.displayName = 'FeaturePaneShell';
